import fetchStoresData from "../lib/fetch-stores-data";

/**
 * Store Location Filter
 * Manages store location selection and filtering for collection pages
 */

function storeLocationFilter() {
  const storage = window.localStorage;
  const SELECTED_STORE_KEY = "selectedStore";

  let selectedStore = null;
  let closestStore = null;
  let fetchPromise = null;

  function parseStore(rawValue, label) {
    if (!rawValue) return null;
    try {
      return JSON.parse(rawValue);
    } catch (err) {
      console.warn(`Error parsing ${label}:`, err);
      return null;
    }
  }

  function persistStore(store) {
    storage?.setItem(SELECTED_STORE_KEY, JSON.stringify(store));
  }

  /**
   * Get selected store from localStorage (sync).
   * selectedStore is canonical; pickupStoreLocation is legacy compatibility.
   */
  function getStoreFromStorage() {
    return parseStore(storage?.getItem(SELECTED_STORE_KEY), SELECTED_STORE_KEY);
  }

  /**
   * Initialize store location filter
   */
  function init() {
    // First check localStorage - don't overwrite existing user selection.
    const storedSelection = getStoreFromStorage();
    if (storedSelection) {
      selectedStore = storedSelection;
      persistStore(storedSelection);
      return;
    }

    // Try to get closest store from session storage (set by store locator)
    fetchPromise = fetchStoresData().then(() => {
      const nearestStoresJson = window.sessionStorage?.getItem("nearestStoresData");
      if (nearestStoresJson) {
        try {
          const nearestStores = JSON.parse(nearestStoresJson);
          if (nearestStores && nearestStores.length > 0) {
            closestStore = nearestStores[0];
            selectedStore = closestStore;
            persistStore(closestStore);
            window.dispatchEvent(new CustomEvent("nearestStoresUpdated", {
              detail: { stores: nearestStores }
            }));
            return;
          }
        } catch (error) {
          console.warn("Error parsing nearest stores:", error);
        }
      }

      // Retry once after nearest-store fetch; storage may be populated by other scripts.
      const storedSelectionAfterFetch = getStoreFromStorage();
      if (storedSelectionAfterFetch) {
        selectedStore = storedSelectionAfterFetch;
        persistStore(storedSelectionAfterFetch);
      }
    });
  }


  /**
   * Set selected store location (manual selection)
   */
  function setSelectedStore(store) {
    if (!store) {
      clearSelectedStore();
      return;
    }
    selectedStore = store;
    persistStore(store);

    // Dispatch event for other components to listen
    window.dispatchEvent(new CustomEvent("storeLocationChanged", {
      detail: { store }
    }));
  }

  /**
   * Get selected store location
   */
  function getSelectedStore() {
    const latestStore = getStoreFromStorage();
    if (latestStore) {
      selectedStore = latestStore;
    } else {
      selectedStore = null;
    }
    return selectedStore;
  }

  /**
   * Get closest store (if known)
   */
  function getClosestStore() {
    return closestStore;
  }

  /**
   * Get nearest store, awaiting the location fetch if it hasn't resolved yet.
   * Returns a Promise that resolves with the nearest store or null.
   */
  function getNearestStore() {
    // Check sessionStorage synchronously first
    const nearestStoresJson = window.sessionStorage?.getItem("nearestStoresData");
    if (nearestStoresJson) {
      try {
        const nearestStores = JSON.parse(nearestStoresJson);
        if (nearestStores && nearestStores.length > 0) {
          return Promise.resolve(nearestStores[0]);
        }
      } catch (e) {
        // fall through to await fetch
      }
    }

    // If fetch is in progress, wait for it then check again
    if (fetchPromise) {
      return fetchPromise.then(() => {
        const json = window.sessionStorage?.getItem("nearestStoresData");
        if (json) {
          try {
            const stores = JSON.parse(json);
            if (stores && stores.length > 0) {
              return stores[0];
            }
          } catch (e) {
            // ignore
          }
        }
        return null;
      });
    }

    return Promise.resolve(null);
  }

  /**
   * Get the full nearest-stores list, awaiting the location fetch if it hasn't
   * resolved yet. Returns a Promise that resolves with the array (possibly empty).
   */
  function getNearestStores() {
    const nearestStoresJson = window.sessionStorage?.getItem("nearestStoresData");
    if (nearestStoresJson) {
      try {
        const nearestStores = JSON.parse(nearestStoresJson);
        if (Array.isArray(nearestStores) && nearestStores.length > 0) {
          return Promise.resolve(nearestStores);
        }
      } catch (e) {
        // fall through to await fetch
      }
    }

    if (fetchPromise) {
      return fetchPromise.then(() => {
        const json = window.sessionStorage?.getItem("nearestStoresData");
        if (json) {
          try {
            const stores = JSON.parse(json);
            if (Array.isArray(stores)) {
              return stores;
            }
          } catch (e) {
            // ignore
          }
        }
        return [];
      });
    }

    return Promise.resolve([]);
  }

  /**
   * Check if closest store is known
   */
  function isClosestStoreKnown() {
    return closestStore !== null;
  }

  /**
   * Clear selected store
   */
  function clearSelectedStore() {
    selectedStore = null;
    storage?.removeItem(SELECTED_STORE_KEY);

    window.dispatchEvent(new CustomEvent("storeLocationChanged", {
      detail: { store: null }
    }));
  }

  // Initialize on load
  init();

  return {
    init,
    setSelectedStore,
    getSelectedStore,
    getClosestStore,
    getStoreFromStorage,
    getNearestStore,
    getNearestStores,
    isClosestStoreKnown,
    clearSelectedStore
  };
}

// Create singleton instance
const storeLocationFilterInstance = storeLocationFilter();

export default storeLocationFilterInstance;
